import React, { useState, useEffect, useRef } from 'react';
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { performLogout } from '../../utils/logoutHandler';
import { useSessionGuard, ExistingSessionModal, SessionConflictModal } from './SessionGuard';
import { SUPPORT_EMAIL } from '../../config/supportContacts';
const API_URL = process.env.REACT_APP_API_URL;

const LANGUAGE_OPTIONS = [
  { code: 'en', labelKey: 'lang.en', defaultValue: 'English' },
  { code: 'hi', labelKey: 'lang.hi', defaultValue: 'हिन्दी' },
  { code: 'ar', labelKey: 'lang.ar', defaultValue: 'العربية' },
];

const SuperadminLoginPage = () => {
  const { t, i18n } = useTranslation(['superadmin_login', 'common']);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const languageMenuRef = useRef(null);
  const currentLanguage = LANGUAGE_OPTIONS.find((l) => l.code === i18n.language) || LANGUAGE_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectLanguage = (code) => {
    localStorage.setItem('cara_lang', code);
    i18n.changeLanguage(code);
    setShowLanguageMenu(false);
  };

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(""); // State for success/error message
  const [ipAddress, setIpAddress] = useState(""); // State to store IP address
  const [resetmessage, setResetMessage] = useState(""); // State for success/error message
  const [resetloading, setResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0); // Track failed login attempts
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false); // Control deactivated modal visibility

  const {
    existingSession,
    showExistingSessionModal,
    setShowExistingSessionModal,
    error,
    setError,
    showErrorModal,
    showLogoutButton,
    setShowLogoutButton,
    handleSessionConflict,
    logoutExistingSession,
    closeErrorModal,
  } = useSessionGuard('SUPERADMIN');

  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/i.test(userAgent)) return "Mobile";
    if (/tablet|ipad/i.test(userAgent)) return "Tablet";
    return "Desktop";
  };

  // Fetch public IPv4 address on component mount
  useEffect(() => {
    axios.get("https://api4.ipify.org?format=json")
      .then(response => setIpAddress(response.data.ip))
      .catch(error => console.error("Error fetching IP:", error));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setShowLogoutButton(false); // Reset logout button state when attempting new login
    setLoading(true); // Set loading to true when starting the login request
    try {
      const response = await axios.post(`${API_URL}api/superadmin_login/`, {
        username,
        password,
        ip_address: ipAddress,
        device_type: getDeviceType(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }, { withCredentials: true }); // Allows Django session to be stored
      console.log("ipAddress =>", ipAddress);

      if (response.status === 200) {
        // Reset failed attempts on successful login
        setFailedAttempts(0);
        setShowLogoutButton(false); // Reset logout button state

        // Store the username in localStorage after successful login
        localStorage.setItem("username", response.data.username);
        localStorage.setItem("user_id", response.data.user_id);
        localStorage.setItem("role", response.data.role || "SUPERADMIN");
        localStorage.setItem("email", response.data.email || "");
        localStorage.setItem("first_name", response.data.first_name || "");
        localStorage.setItem("last_name", response.data.last_name || "");
        sessionStorage.setItem("loginType", "superadmin");

        setMessage(t('msg.loginSuccessRedirecting', { defaultValue: 'Login successful! Redirecting...' }));
        console.log("Device Type:", getDeviceType());

        // Redirect to OTP validation page
        setTimeout(() => {
          window.location.replace(`/otp?user_type=SUPERADMIN`);
        }, 1000);
      }
    } catch (error) {
      if (error.response) {
        // Handle 403 - User already logged in from another location
        if (handleSessionConflict(error)) {
          return;
        }

        // Backend returned an error (400/500)
        const errorMessage = error.response.data.message || t('msg.invalidCredentials', { defaultValue: 'Invalid credentials. Please try again.' });

        if (error.response.data.status === "DEACTIVATED") {
          setShowDeactivatedModal(true);
          setLoading(false);
          return;
        }

        setError(errorMessage);

        // Increment failed attempts
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        // Show alert after 3 failed attempts
        if (newFailedAttempts >= 3) {
          alert(t('msg.forgotPasswordHint', { defaultValue: 'Forgot your password? Click the "Forgot Password" button to reset it.' }));
          setFailedAttempts(0); // Reset counter after showing alert
        }
      } else {
        // Network or unexpected error
        setError(t('msg.unexpectedError', { defaultValue: 'An unexpected error occurred. Please try again.' }));
      }
    } finally {
      setLoading(false); // Set loading to false when the request finishes
    }
  };

  const handleForgotPassword = async () => {
    setResetLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}api/forgot_password/`, {
        username: username,
        user_type: "superadmin" // User type for superadmin login page
      }, { withCredentials: true });

      setResetMessage(response.data.resetmessage);
      alert(t('msg.resetLinkSent', { defaultValue: 'Reset Password link has been sent to your email ID.' }));
      setUsername("");  // clear input
      window.location.reload();
    } catch (err) {
      if (err.response?.data?.status === "DEACTIVATED") {
        setShowDeactivatedModal(true);
        return;
      }
      setError(err.response?.data?.message || t('msg.somethingWentWrong', { defaultValue: 'Something went wrong!' }));
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Get username from localStorage to update statistics
      const username = localStorage.getItem('username');

      // Call backend logout API
      console.log('📞 Calling backend logout API...');
      await axios.post(`${API_URL}api/logout/`, { username }, { withCredentials: true });
      console.log('Backend logout API successful');

      // Use centralized logout handler for cross-tab synchronization
      // This is SUPERADMIN login page, so redirect to superadmin login
      await performLogout('SUPERADMIN', null, true);

    } catch (err) {
      console.warn("Logout error:", err);
      // Even if logout API fails, use centralized logout
      await performLogout('SUPERADMIN', null, true);
    }
  };

  const handleLogoutExistingSession = async () => {
    // Invalidates the existing server session, clears client-side cache,
    // then lets the user continue with a fresh login attempt.
    const result = await logoutExistingSession(username);
    if (result.success) {
      setMessage(result.message);
      setPassword("");
    }
  };

  const handleContinueWithExistingSession = () => {
    setShowExistingSessionModal(false);
    // Redirect to appropriate dashboard based on role
    if (existingSession?.role === 'SUPERADMIN') {
      window.location.replace('/otp?user_type=SUPERADMIN');
    } else {
      // Redirect to appropriate dashboard for other roles
      window.location.replace('/assessment_dashboard');
    }
  };

  return (
    <div className="">
      <main id="superadmin_login">
        <div className="d-flex align-items-center justify-content-center p-2" style={{ minHeight: '100vh', backgroundColor: '#e2e8f0' }}>
          <div className="card shadow-lg border-0 w-100" style={{ maxWidth: '1200px', borderRadius: '16px', overflow: 'hidden' }}>
            <div className="row g-0" style={{ minHeight: '85vh' }}>
              {/* Left Section (Marketing) */}
              <div className="col-md-5 d-flex flex-column p-5 position-relative" style={{ backgroundColor: '#f4f9ff' }}>
                <div style={{
                  position: 'absolute', top: '-10%', right: '-10%', width: '60%', height: '60%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(244,249,255,0) 70%)',
                  borderRadius: '50%', zIndex: 0
                }}></div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(to top, rgba(13,110,253,0.1), transparent)', zIndex: 0 }}></div>
                
                <div style={{ zIndex: 1 }} className="d-flex flex-column h-100 justify-content-center">
                  <div>
                    {/* Logo Area */}
                    <div className="mb-4 d-flex align-items-center">
                      <i className="fas fa-shield-alt text-primary fs-2 me-2"></i>
                      <h2 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>CARA<span style={{ fontWeight: 'normal', color: '#475569' }}>grc</span></h2>
                    </div>

                    <h1 className="fw-bold mb-3" style={{ color: '#1e293b', fontSize: '2.5rem' }}>
                      {t('title.globalCommandCenter', { defaultValue: 'Global Command Center' })}
                    </h1>
                    <div style={{ width: '40px', height: '4px', backgroundColor: '#0d6efd', borderRadius: '2px', marginBottom: '1.5rem' }}></div>
                    
                    <p className="fs-5 mb-4" style={{ color: '#475569', maxWidth: '90%' }}>
                      {t('desc.manageTenants', { defaultValue: 'Manage tenants, access controls, and system configurations across the entire platform.' })}
                    </p>

                    <div className="mt-5">
                      <div className="p-4 rounded-4 shadow-sm" style={{ backgroundColor: 'rgba(255,255,255,0.7)', border: '1px solid #e2e8f0', backdropFilter: 'blur(10px)', maxWidth: '90%' }}>
                        <div className="d-flex mb-4">
                          <div className="bg-primary bg-opacity-10 rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '45px', height: '45px', minWidth: '45px' }}>
                            <i className="fas fa-network-wired text-primary fs-5"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1" style={{ color: '#0f172a' }}>{t('title.globalAdministration', { defaultValue: 'Global Administration' })}</h6>
                            <p className="small mb-0" style={{ color: '#475569', lineHeight: '1.5' }}>{t('desc.fullOversight', { defaultValue: 'Full oversight of all system modules and client instances from a single pane of glass.' })}</p>
                          </div>
                        </div>
                        <div className="d-flex">
                          <div className="bg-primary bg-opacity-10 rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '45px', height: '45px', minWidth: '45px' }}>
                            <i className="fas fa-sliders-h text-primary fs-5"></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1" style={{ color: '#0f172a' }}>{t('title.granularControl', { defaultValue: 'Granular Control' })}</h6>
                            <p className="small mb-0" style={{ color: '#475569', lineHeight: '1.5' }}>{t('desc.configureHierarchy', { defaultValue: 'Configure organizational hierarchy, workflows, and strict role-based access policies.' })}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section (Form) */}
              <div className="col-md-7 d-flex align-items-center justify-content-center p-4 p-md-5" style={{ backgroundColor: '#f1f5f9' }}>
                <div className="card border-0 w-100" style={{ maxWidth: '450px', borderRadius: '16px', backgroundColor: '#ffffff', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }}>
                  <div className="card-body p-4 p-md-5">
                    <div className="text-center mb-5">
                      <h4 className="fw-bold mb-2 text-nowrap" style={{ color: '#0f172a', fontSize: '1.35rem' }}>{t('title.superAdminWelcome', { defaultValue: 'CARAgrc Super AdminPortal' })}</h4>
                      <p className="text-muted mb-0">{t('title.subheading', { defaultValue: 'You are accessing your CARA GRC - Compliance & Risk Management Account.' })}</p>
                    </div>

                  <form onSubmit={handleLogin}>
                    <div className="form-group mb-4">
                      <label className="form-label fw-semibold text-muted small mb-2">{t('placeholder.userId', { defaultValue: 'Your User Id' })}</label>
                      <div className="position-relative">
                        <span className="position-absolute top-50 translate-middle-y" style={{ left: '16px', color: '#64748b' }}>
                          <i className="fas fa-user"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-lg bg-light"
                          style={{ paddingLeft: '45px', fontSize: '1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                          placeholder={t('placeholder.userId', { defaultValue: 'Your User Id' })}
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          required 
                        />
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label className="form-label fw-semibold text-muted small mb-2">{t('placeholder.password', { defaultValue: 'Password' })}</label>
                      <div className="position-relative">
                        <span className="position-absolute top-50 translate-middle-y" style={{ left: '16px', color: '#64748b' }}>
                          <i className="fas fa-lock"></i>
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control form-control-lg bg-light"
                          style={{ paddingLeft: '45px', paddingRight: '45px', fontSize: '1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                          placeholder={t('placeholder.password', { defaultValue: 'Password' })}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          required
                        />
                        <span 
                          className="position-absolute top-50 translate-middle-y" 
                          style={{ right: '16px', color: '#64748b', cursor: 'pointer' }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </span>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mb-4">
                      <a href="#" className="text-decoration-none fw-semibold small" style={{ color: '#0d6efd' }} data-bs-toggle="modal" data-bs-target="#forgotPasswordModal">
                        {t('link.forgotPassword', { defaultValue: 'Forgot password?' })}
                      </a>
                    </div>

                    <button 
                      type="submit" 
                      className="btn btn-primary w-100 py-3 rounded-3 fw-bold fs-6 shadow-sm" 
                      disabled={loading}
                      style={{ 
                        background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                        border: 'none',
                        transition: 'transform 0.1s ease, box-shadow 0.1s ease'
                      }}
                    >
                      {loading ? t('btn.signingIn', { defaultValue: 'Signing In...' }) : t('btn.signIn', { defaultValue: 'Sign In' })}
                    </button>
                    {message && <p className="mt-3 text-success text-center fw-semibold">{message}</p>}
                  </form>

                  <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />

                  {/* Footer */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="position-relative" ref={languageMenuRef}>
                      <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center gap-2"
                        onClick={() => setShowLanguageMenu((prev) => !prev)}
                        style={{
                          color: '#475569',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '6px 12px',
                          backgroundColor: showLanguageMenu ? '#f8fafc' : 'transparent',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-globe text-muted"></i>
                        <span>{t(currentLanguage.labelKey, { ns: 'common', defaultValue: currentLanguage.defaultValue })}</span>
                        <i className={`fas fa-chevron-${showLanguageMenu ? 'up' : 'down'} text-muted`} style={{ fontSize: '0.7rem' }}></i>
                      </button>

                      {showLanguageMenu && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            marginBottom: '0.5rem',
                            minWidth: '160px',
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                            padding: '4px',
                            zIndex: 1050,
                          }}
                        >
                          {LANGUAGE_OPTIONS.map((option) => {
                            const isActive = i18n.language === option.code;
                            return (
                              <button
                                type="button"
                                key={option.code}
                                onClick={() => handleSelectLanguage(option.code)}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '6px 10px',
                                  marginBottom: '2px',
                                  fontWeight: isActive ? 700 : 500,
                                  fontSize: '0.85rem',
                                  color: isActive ? '#fff' : '#1f2937',
                                  background: isActive ? '#0d6efd' : 'transparent',
                                  cursor: 'pointer',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                              >
                                {t(option.labelKey, { ns: 'common', defaultValue: option.defaultValue })}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <span className="text-muted small">{t('text.needHelp', { defaultValue: 'Need help? ' })}</span>
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-decoration-none small fw-semibold" style={{ color: '#0d6efd' }}>{t('btn.contactSupport', { defaultValue: 'Contact Support' })}</a>
                    </div>
                  </div>

                  </div>
                </div>
              </div>

              {/* Forgot Password Modal */}
              <div className="modal fade" id="forgotPasswordModal" tabIndex="-1" aria-labelledby="forgotPasswordLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
                    <div className="modal-header border-bottom-0 pb-0">
                      <h5 className="modal-title fw-bold" id="forgotPasswordLabel">{t('modal.forgotPassword.title', { defaultValue: 'Forgot Password?' })}</h5>
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body text-center pt-2 pb-4">
                      <p className="text-muted small mb-4">{t('modal.forgotPassword.desc', { defaultValue: 'Enter your user ID to receive a password reset link.' })}</p>
                      <div className="form-group mb-3">
                        <input
                          type="text"
                          className="form-control form-control-lg bg-light"
                          placeholder={t('placeholder.enterUsername', { defaultValue: 'Enter Your User ID' })}
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          style={{ borderRadius: '8px' }} 
                        />
                      </div>
                      {error && <p className="text-danger mt-2 mb-0 small">{error}</p>}
                      {resetmessage && <p className="text-success mt-2 mb-0 small">{resetmessage}</p>}
                      <button
                        type="button"
                        className="btn btn-primary w-100 py-2 mt-3 fw-semibold"
                        onClick={handleForgotPassword}
                        disabled={resetloading}
                        style={{ borderRadius: '8px' }}
                      >
                        {resetloading ? t('btn.sending', { defaultValue: 'Sending...' }) : t('btn.send', { defaultValue: 'Send Reset Link' })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Existing Session Modal */}
      <ExistingSessionModal
        show={showExistingSessionModal}
        existingSession={existingSession}
        onLogout={handleLogout}
        onContinue={handleContinueWithExistingSession}
      />

      {/* Session Conflict / Login Error Modal */}
      <SessionConflictModal
        show={showErrorModal}
        error={error}
        showLogoutButton={showLogoutButton}
        onClose={closeErrorModal}
        onLogoutExisting={handleLogoutExistingSession}
      />

      {/* Deactivated Modal */}
      {showDeactivatedModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
              <div className="modal-header bg-danger text-white" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-x-fill me-2"></i>
                  {t('modal.deactivated.title', { defaultValue: 'Account Deactivated' })}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeactivatedModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4 text-center">
                <div className="mb-4">
                  <i className="bi bi-exclamation-octagon text-danger" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="fw-bold mb-3">{t('modal.deactivated.heading', { defaultValue: 'Login Restricted' })}</h4>
                <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                  {t('modal.deactivated.messagePrefix', { defaultValue: 'Your account has been' })} <strong>{t('modal.deactivated.deactivatedWord', { defaultValue: 'deactivated' })}</strong>. {t('modal.deactivated.messageSuffix', { defaultValue: 'You cannot login at this time.' })}
                </p>
                <div className="bg-light p-3 rounded-4 mb-4">
                  <p className="mb-0 text-dark">
                    {t('modal.deactivated.contactPrefix', { defaultValue: 'Please contact our support team at' })} <strong>{SUPPORT_EMAIL}</strong> {t('modal.deactivated.contactSuffix', { defaultValue: 'or your administrator to reactivate your account.' })}
                  </p>
                </div>
              </div>
              <div className="modal-footer bg-light p-3" style={{ borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px' }}>
                <button
                  type="button"
                  className="btn btn-secondary px-4 shadow-sm"
                  onClick={() => setShowDeactivatedModal(false)}
                >
                  {t('btn.close', { ns: 'common', defaultValue: 'Close' })}
                </button>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-danger px-4 shadow-sm">
                  {t('btn.contactSupport', { defaultValue: 'Contact Support' })}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivated Modal Backdrop */}
      {showDeactivatedModal && (
        <div className="modal-backdrop fade show"></div>
      )}

    </div>
  );
};

export default SuperadminLoginPage;
